export const CHARACTERIZATION_LOADING_FUN_KEYS = [
  "dashboard.characterizationLoadingFunPreparing",
  "dashboard.characterizationLoadingFunSorting",
  "dashboard.characterizationLoadingFunNothingLost",
  "dashboard.characterizationLoadingFunCompact",
  "dashboard.characterizationLoadingFunCharts",
  "dashboard.characterizationLoadingFunLoading",
  "dashboard.characterizationLoadingFunAlmost",
];

export const CHARACTERIZATION_LOADING_PROGRESS_KEYS = [
  "dashboard.characterizationLoadingProgress",
  "dashboard.characterizationLoadingProgressNous",
  "dashboard.characterizationLoadingProgressRemaining",
  "dashboard.characterizationLoadingProgressLeft",
  "dashboard.characterizationLoadingProgressRatio",
];

export const MESSAGE_KIND = {
  TEXT: "text",
  PROGRESS: "progress",
};

const REMAINING_PROGRESS_KEYS = new Set([
  "dashboard.characterizationLoadingProgressRemaining",
  "dashboard.characterizationLoadingProgressLeft",
]);

const shuffleKeys = (keys, random = Math.random) => {
  const shuffled = [...keys];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = shuffled[index];
    shuffled[index] = shuffled[swapIndex];
    shuffled[swapIndex] = current;
  }
  return shuffled;
};

const drawKeyWithoutReplacement = (
  remaining,
  previousKey,
  allKeys,
  random = Math.random,
) => {
  let bag =
    remaining.length > 0 ? [...remaining] : shuffleKeys(allKeys, random);

  if (bag.length > 1 && bag[0] === previousKey) {
    const swapIndex = bag.findIndex((key) => key !== previousKey);
    if (swapIndex > 0) {
      const current = bag[0];
      bag[0] = bag[swapIndex];
      bag[swapIndex] = current;
    }
  }

  const [nextKey, ...nextRemaining] = bag;
  return { key: nextKey, remaining: nextRemaining };
};

export const drawCharacterizationLoadingFunKey = (
  remaining = [],
  previousKey,
  random = Math.random,
) =>
  drawKeyWithoutReplacement(
    remaining,
    previousKey,
    CHARACTERIZATION_LOADING_FUN_KEYS,
    random,
  );

const drawCharacterizationLoadingProgressKey = (
  remaining = [],
  previousKey,
  random = Math.random,
) =>
  drawKeyWithoutReplacement(
    remaining,
    previousKey,
    CHARACTERIZATION_LOADING_PROGRESS_KEYS,
    random,
  );

const getCharacterizationProgressAnimatedValue = ({
  templateKey,
  loaded = 0,
  total = 0,
} = {}) => {
  if (REMAINING_PROGRESS_KEYS.has(templateKey)) {
    return Math.max(0, total - loaded);
  }
  return Math.max(0, loaded);
};

export const buildCharacterizationProgressMessage = ({
  templateKey,
  loaded,
  total,
  t,
} = {}) => {
  if (typeof t !== "function" || !templateKey) return "";
  const value = getCharacterizationProgressAnimatedValue({
    templateKey,
    loaded,
    total,
  });

  if (REMAINING_PROGRESS_KEYS.has(templateKey)) {
    return t(templateKey, { count: value });
  }

  return t(templateKey, {
    count: value,
    total,
  });
};

/** Split a progress sentence so the animated value can render as rolling digits. */
export const splitProgressSentenceForOdometer = (sentence, animatedValue) => {
  if (!sentence) return [];
  const token = String(animatedValue);
  let from = 0;
  let index = -1;
  while (from <= sentence.length) {
    const found = sentence.indexOf(token, from);
    if (found === -1) break;
    const before = found === 0 ? "" : sentence[found - 1];
    const after = sentence[found + token.length] || "";
    if (!/\d/.test(before) && !/\d/.test(after)) {
      index = found;
      break;
    }
    from = found + 1;
  }
  if (index < 0) {
    return [{ type: "text", value: sentence }];
  }
  return [
    { type: "text", value: sentence.slice(0, index) },
    { type: "number", value: animatedValue },
    { type: "text", value: sentence.slice(index + token.length) },
  ];
};

export const buildCharacterizationLoadingMessage = ({
  count,
  loaded = 0,
  periodLabel,
  t,
} = {}) => {
  if (typeof t !== "function") return "";
  if (count == null) {
    return t("dashboard.characterizationLoadingStarting");
  }
  if (count === 0) return "";
  if (loaded <= 0) {
    return t("dashboard.characterizationLoadingListed", {
      count,
      period: periodLabel || "",
    });
  }
  return t("dashboard.characterizationLoadingProgress", {
    count: loaded,
    total: count,
  });
};

const buildProgressFrame = ({
  templateKey,
  loaded,
  total,
  t,
  progressBag,
  lastFunKey,
  funBag,
}) => {
  const value = getCharacterizationProgressAnimatedValue({
    templateKey,
    loaded,
    total,
  });
  const text = buildCharacterizationProgressMessage({
    templateKey,
    loaded,
    total,
    t,
  });
  return {
    kind: MESSAGE_KIND.PROGRESS,
    templateKey,
    loaded,
    total,
    value,
    text,
    lastFactual: text,
    lastWasFun: false,
    lastFunKey,
    funBag,
    progressKey: templateKey,
    progressBag,
  };
};

export const nextCharacterizationLoadingTurn = ({
  liveMessage,
  loaded = 0,
  total = 0,
  lastFactual,
  lastWasFun,
  lastFunKey,
  funBag = [],
  progressKey = null,
  progressBag = [],
  t,
  random = Math.random,
}) => {
  if (!liveMessage) return null;

  const hasProgressCounts = total > 0 && loaded > 0;

  if (hasProgressCounts) {
    // After a progress dwell, switch to a fun aside before the next progress phrase.
    if (!lastWasFun && progressKey != null) {
      const drawn = drawCharacterizationLoadingFunKey(
        funBag,
        lastFunKey,
        random,
      );
      return {
        kind: MESSAGE_KIND.TEXT,
        text: t(drawn.key),
        lastFactual,
        lastWasFun: true,
        lastFunKey: drawn.key,
        funBag: drawn.remaining,
        progressKey: null,
        progressBag,
      };
    }

    const drawn = drawCharacterizationLoadingProgressKey(
      progressBag,
      progressKey,
      random,
    );

    return buildProgressFrame({
      templateKey: drawn.key,
      loaded,
      total,
      t,
      progressBag: drawn.remaining,
      lastFunKey,
      funBag,
    });
  }

  if (!lastWasFun || liveMessage === lastFactual) {
    const drawn = drawCharacterizationLoadingFunKey(funBag, lastFunKey, random);
    return {
      kind: MESSAGE_KIND.TEXT,
      text: t(drawn.key),
      lastFactual,
      lastWasFun: true,
      lastFunKey: drawn.key,
      funBag: drawn.remaining,
      progressKey: null,
      progressBag,
    };
  }

  return {
    kind: MESSAGE_KIND.TEXT,
    text: liveMessage,
    lastFactual: liveMessage,
    lastWasFun: false,
    lastFunKey,
    funBag,
    progressKey: null,
    progressBag,
  };
};

/** Live count update while holding a progress phrase. */
export const refreshCharacterizationLoadingProgress = ({
  loaded,
  total,
  progressKey,
  progressBag = [],
  lastValue,
  lastFunKey,
  funBag = [],
  t,
}) => {
  if (!progressKey || total <= 0 || loaded <= 0) return null;

  const value = getCharacterizationProgressAnimatedValue({
    templateKey: progressKey,
    loaded,
    total,
  });
  if (value === lastValue) return null;

  return buildProgressFrame({
    templateKey: progressKey,
    loaded,
    total,
    t,
    progressBag,
    lastFunKey,
    funBag,
  });
};
